// Pure pending-run bookkeeping for the recurring grid's post-trigger overlay (sc-100769).
//
// The overlay's clear signal is a CHANGED `last_job_id`, not a changed run state: Hangfire mints a new
// run id per execution, whereas a job that finishes fast and lands back on the same terminal state
// produces no observable state change and would leave the row pending forever. This module therefore
// compares run ids only and never reads the run-state field at all — the predicate that does read it is
// `shouldPoll` in `job-states.ts`.
//
// No React, no fetch: every transition is a plain function of (map, rows, clock).

/** 10 poll cycles. The bounded escape hatch, not a correctness dependency. */
export const PENDING_TIMEOUT_MS = 30_000;

export interface PendingRun {
  /** The row's run id at trigger time — the value a later fetch has to move away from. */
  readonly lastJobId: string | null;
  /**
   * Whether that run id was actually OBSERVED at trigger time.
   *
   * `false` means the row was absent from the last successful fetch — the mount fetch failed, or the
   * dialog was submitted before the first fetch landed. `lastJobId` is then `null` because nothing is
   * *known*, which is indistinguishable by value from a genuinely never-run job, so the first fetch
   * showing a real id must NOT be read as settlement: doing that clears the overlay while the run is
   * still in flight and the cell falls back to the previous run's terminal state — the defect this
   * story exists to fix. An unknown baseline is bounded by the escape hatch alone.
   *
   * A *seen* `null` is different and still settles on null → id: that is a never-run job's real signal.
   */
  readonly observed: boolean;
  readonly startedAt: number;
}

/** The trigger-time baseline: the row's run id, and whether it was actually seen. */
export interface PendingBaseline {
  readonly lastJobId: string | null;
  readonly observed: boolean;
}

export type PendingRuns = ReadonlyMap<string, PendingRun>;

/**
 * Records the row's CURRENT `last_job_id` at trigger time. Called only after an accepted (202) trigger,
 * so a rejected one can never leave a row pending.
 */
export function markPending(
  pending: PendingRuns,
  recurringJobId: string,
  baseline: PendingBaseline,
  now: number
): PendingRuns {
  const next = new Map(pending);
  next.set(recurringJobId, {
    lastJobId: baseline.lastJobId,
    observed: baseline.observed,
    startedAt: now,
  });
  return next;
}

/**
 * Clears an entry when the fetched row's `last_job_id` has MOVED, or when the bounded escape hatch has
 * elapsed.
 *
 * Passing an empty `rows` array clears nothing by id but still ages entries out by `timeoutMs`, which
 * is what the failing-refresh and hung-request paths rely on. Returns the same map when nothing
 * changed, so it is safe to call on every tick.
 */
export function clearSettled(
  pending: PendingRuns,
  rows: readonly { id: string; last_job_id: string | null }[],
  now: number,
  timeoutMs: number
): PendingRuns {
  if (pending.size === 0) return pending;

  const fetched = new Map(rows.map((row) => [row.id, row.last_job_id]));
  const next = new Map(pending);

  for (const [recurringJobId, entry] of pending) {
    // An id move only settles an entry whose baseline was actually observed — see PendingRun.observed.
    const moved =
      entry.observed &&
      fetched.has(recurringJobId) &&
      fetched.get(recurringJobId) !== entry.lastJobId;
    const expired = now - entry.startedAt >= timeoutMs;
    if (moved || expired) next.delete(recurringJobId);
  }

  return next.size === pending.size ? pending : next;
}
